// Pure helpers for measuring on a Site Progress map picture (distance, area,
// elevation). Scale comes from the API's `measure` block on a deliverable:
//   { source: 'geotiff', width, height, pixel_m: [x, y] }  read from the GeoTIFF
//   { source: 'entered', ground_width_m }                  set by Bricks ops
// The picture is assumed to show the whole map; its shape must match.

const ASPECT_TOLERANCE = 0.03;

export function scaleFor(measure, naturalW, naturalH) {
  if (!measure || !naturalW || !naturalH) return { error: 'Measuring is off for this file.' };
  if (measure.source === 'geotiff') {
    const { width, height, pixel_m: px } = measure;
    if (!width || !height || !px) return { error: 'Measuring is off for this file.' };
    const mapAspect = width / height;
    if (Math.abs(naturalW / naturalH - mapAspect) / mapAspect > ASPECT_TOLERANCE) {
      return { error: "This preview doesn't match the map's shape, so measuring is off." };
    }
    return { mx: (px[0] * width) / naturalW, my: (px[1] * height) / naturalH, source: 'geotiff' };
  }
  if (measure.source === 'entered' && measure.ground_width_m > 0) {
    const m = measure.ground_width_m / naturalW;
    return { mx: m, my: m, source: 'entered' };
  }
  return { error: 'Measuring is off for this file.' };
}

export function pathLength(points, scale) {
  let total = 0;
  for (let i = 1; i < (points || []).length; i += 1) {
    const dx = (points[i].x - points[i - 1].x) * scale.mx;
    const dy = (points[i].y - points[i - 1].y) * scale.my;
    total += Math.hypot(dx, dy);
  }
  return total;
}

export function polygonArea(points, scale) {
  const p = points || [];
  if (p.length < 3) return 0;
  let s = 0;
  for (let i = 0; i < p.length; i += 1) {
    const a = p[i];
    const b = p[(i + 1) % p.length];
    s += a.x * scale.mx * (b.y * scale.my) - b.x * scale.mx * (a.y * scale.my);
  }
  return Math.abs(s) / 2;
}

const n1 = (v, d) => v.toLocaleString('en-CA', { minimumFractionDigits: d, maximumFractionDigits: d });

export function fmtLength(m) {
  if (!Number.isFinite(m)) return '—';
  if (m >= 1000) return `${n1(m / 1000, 2)} km`;
  if (m >= 100) return `${n1(m, 0)} m`;
  return `${n1(m, 1)} m`;
}

export function fmtArea(m2) {
  if (!Number.isFinite(m2)) return '—';
  if (m2 >= 10000) return `${n1(m2 / 10000, 2)} ha`;
  return `${n1(m2, 0)} m²`;
}

export function fmtElevation(m) {
  if (m === null || m === undefined || !Number.isFinite(Number(m))) return 'No data';
  return `${n1(Number(m), 1)} m`;
}

// Elevation difference between the last two readings ("Rise 3.2 m" / "Drop 1.0 m").
export function elevationChange(readings) {
  const ok = (readings || []).filter((r) => Number.isFinite(r.value));
  if (ok.length < 2) return '';
  const d = ok[ok.length - 1].value - ok[ok.length - 2].value;
  if (Math.abs(d) < 0.05) return 'Level';
  return `${d > 0 ? 'Rise' : 'Drop'} ${n1(Math.abs(d), 1)} m`;
}

// Click position on the shown image -> natural pixel position.
export function toNatural(clientX, clientY, rect, naturalW, naturalH) {
  const fx = (clientX - rect.left) / rect.width;
  const fy = (clientY - rect.top) / rect.height;
  if (!(fx >= 0 && fx <= 1 && fy >= 0 && fy <= 1)) return null;
  return { x: fx * naturalW, y: fy * naturalH, fx, fy };
}
